import React, {Key, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux'
import clsx from 'clsx'
import { Skeleton } from '@mui/material'

import {submitSmsCodeAgain} from '../../store/thunk-actions'
import {
    ICodeVerificationFormSendSmsCodeAgainProps,
    ICodeVerificationInputProps,
    IStatusTextProps
} from './ContractSigningPageProps'
import useTextContents from '../../hooks/useTextContents'
import {SEND_AGAIN_SMS_CODE_STATUSES, SMS_CODE_STATUSES} from '../../store/reducers/contract-sign-reducer'
import {TVerificationCodeInputsNamesModel, verificationCodeInputsNames} from '../../store/types'
import {RESET_VERIFICATION_CODE_FORM, SET_VERIFICATION_CODE_INPUT_VALUE} from '../../store/actions/code-actions'
import useCodeVerificationInputLogic from './hooks/useCodeVerificationInputLogic'
import useContractSigningPageLogic from './hooks/useContractSigningPageLogic'
import useSendSmsCodeAgainLogic from './hooks/useSendSmsCodeAgainLogic'
import HiddenInput from '../../components/HiddenInput/HiddenInput'
import {AppState} from '../../store'
import {FormNames} from '../../models/form/IFormDefinition'
import {BasicInfoFieldsNames} from '../../formDefinitions/BasicInfo/BasicInfoFields'
import {Environment} from '../../environment/app-settings'
import PageScreen from "../../components/organisms/Page/PageScreen";
import AppButton from "../../components/atoms/AppButton/AppButton";
import {UI_BUTTON_TYPE} from "../../components/atoms/AppButton/AppButtonProps";
import Paragraph from "../../components/atoms/Paragraph/Paragraph";
import LoanOfferSummaryPanel from '../../components/organisms/LoanOfferSummaryPanel/LoanOfferSummaryPanel'
import { selectUserEmailFromState } from '../../store/reducers/customSelectors'


import './ContractSigningPage.scss'
import './safari.css'


const CodeVerificationInput = (props: ICodeVerificationInputProps): JSX.Element  => {

   const { DATASET_INPUT_IDENTIFIER, handleOnClick, handleKeyDown, handleChange, handlePaste } = useCodeVerificationInputLogic(props)

   return (
      props.isLoading ? ( <Skeleton variant='rounded' animation="wave" className='single-code-verification-input-skeleton' /> ) :
      (
         <input
            name={props.name}
            value={props.value}
            id={props.id}
            disabled={props.isLoading}
            maxLength={1}
            autoComplete="off"
            inputMode='numeric'
            input-identifier={DATASET_INPUT_IDENTIFIER} //WARNING - custom attribute - nie zmieniać
            onChange={handleChange}
            onClick={handleOnClick}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            className={"code-verification-input"}
         />
      )
   )
}

const CodeVerificationForm = (props: IStatusTextProps): JSX.Element  => {
   const dispatch = useDispatch()
   const { contractSignState, isLoading, contractSigningPageTextContents } = props
   const { verificationCodeInputs, hasError } = contractSignState

   return (
      <div className='contract-signing-phone-code-verification-wrapper'>
         <Paragraph className='contract-signing-phone-code-heading' align={"center"}>
            {contractSigningPageTextContents('contractSigningPage.smsCode.heading')}
         </Paragraph>
         <form
            className={clsx(
               'contract-signing-phone-code-verification',
               'contract-signing-phone-code-verification-safari-only',
               (hasError) && 'contract-signing-phone-code-verification-form-has-error'
            )}
         >
         {
            Object.values(verificationCodeInputsNames).map((codeVerificationInputName: TVerificationCodeInputsNamesModel, index: Key) => {
               return (
                  <CodeVerificationInput
                     key={index}
                     name={codeVerificationInputName}
                     isLoading={isLoading}
                     handleSaveInputValueToState={(inputName: TVerificationCodeInputsNamesModel, newValue: string) => dispatch({type: SET_VERIFICATION_CODE_INPUT_VALUE, payload: {inputName, newValue}})}
                     id={index.toString()}
                     value={verificationCodeInputs[codeVerificationInputName]}
                     resetForm={() => dispatch({ type: RESET_VERIFICATION_CODE_FORM })}
                  />
               )
            })
         }
         </form>
         <SmsCodeStatusText contractSignState={contractSignState} contractSigningPageTextContents={contractSigningPageTextContents} />
      </div>
	)
}

const SmsCodeStatusText = (props: IStatusTextProps): JSX.Element  => {
   const [currentText, setCurrentText] = useState<string | null>()
   const { contractSignState, contractSigningPageTextContents } = props

   useEffect(() => {
      // Na podstawie obecnego statusu, zmieniamy na właściwy tekst
         switch(contractSignState.currentSmsCodeTextState){
            case SMS_CODE_STATUSES.SEND_ERROR: {
               setCurrentText(contractSigningPageTextContents('contractSigningPage.smsCode.codeResend.sendError'))
               break
            }
            case SMS_CODE_STATUSES.VALIDATION_ERROR_FULL_CODE_REQUIRED: {
               setCurrentText(contractSigningPageTextContents('contractSigningPage.smsCode.validation.required'))
               break
            }
            case SMS_CODE_STATUSES.VALIDATION_ERROR_WRONG_CODE: {
               setCurrentText(contractSigningPageTextContents('contractSigningPage.smsCode.validation.wrong'))
               break
            }
            // case SMS_CODE_STATUSES.ALPHA_NOT_READY: { // PODŁĄCZYĆ W RAZIE KONIECZNOŚCI
            //    setCurrentText(contractSigningPageTextContents('contractSigningPage.smsCode.codeResend.alphaNotReadyError'))
            //    break
            // }
            default: {
               setCurrentText(null)
            }
         }
      }, [contractSignState.currentSmsCodeTextState])


      if(currentText === null || currentText?.length === 0){
         return <p className="contract-signing-error-text" /> // PLACEHOLDER
      }

      if(contractSignState.hasError) {
         return (
            <Paragraph align={"center"} className="contract-signing-error-text">{currentText}</Paragraph>
         )
      }
        //todo - zmiana p - wymaga children lub html
      return (
         <p className="contract-signing-error-text" />
      )
}


const SendAgainSmsCodeStatusText = (props: IStatusTextProps): JSX.Element | null => {
   const [currentText, setCurrentText] = useState<string | null>()
   const { contractSignState, contractSigningPageTextContents } = props

    useEffect(() => {
        if (!currentText) {
            return
        }

        const timer = setTimeout(() => {
            setCurrentText(null)
        }, 15000) //jak dlugo ma byc ten komunikat

        return () => clearTimeout(timer)
    }, [currentText])

   useEffect(() => {
      // Na podstawie obecnego statusu, zmieniamy na właściwy tekst
         switch(contractSignState.currentSendAgainSmsCodeTextState){
            case SEND_AGAIN_SMS_CODE_STATUSES.CODE_SEND_CORRECTLY: {
               setCurrentText(contractSigningPageTextContents('contractSigningPage.smsCode.codeResend.sendCorectly'))
               break
            }
            case SEND_AGAIN_SMS_CODE_STATUSES.LIMIT_REACHED: {
               setCurrentText(contractSigningPageTextContents('contractSigningPage.smsCode.codeResend.limitReached'))
               break
            }
            case SEND_AGAIN_SMS_CODE_STATUSES.SEND_ERROR: {
               setCurrentText(contractSigningPageTextContents('contractSigningPage.smsCode.codeResend.sendError'))
               break
            }
            default: {
               setCurrentText(null)
            }
         }
      }, [contractSignState.currentSendAgainSmsCodeTextState])

      if(currentText === null || currentText?.length === 0){
         return null
      }

      if(contractSignState.hasSmsSendAgainError) {
         return (
            <Paragraph align={"center"} className="contract-signing-page-send-code-again-has-error">{currentText}</Paragraph>
         )
      }

      return (
         <Paragraph align={"center"}  className="contract-signing-page-send-code-again-succes">{currentText}</Paragraph>
      )
}

/**
 * Komponent obsługujący wyświetlanie statusów ponownej wysyłki kodu sms oraz błędów powstałych przy tej okazji.
 */
const CodeVerificationFormSendSmsCodeAgain = (props: ICodeVerificationFormSendSmsCodeAgainProps): JSX.Element  => {

   const { contractSignState, contractSigningPageTextContents, dispatch } = props
   const { canSendSmsAgain, setIsSended, setCanSendSmsAgain } = useSendSmsCodeAgainLogic()

   return (
		<div className='contract-signing-page-send-code-wrapper'>
			<Paragraph className='contract-signing-page-code-not-recived' align={"center"}>
                {contractSigningPageTextContents('contractSigningPage.smsCode.tipQuestion')}
            </Paragraph>
			<AppButton
                uiButtonType={UI_BUTTON_TYPE.PRIMARY_OUTLINE}
				disabled={!canSendSmsAgain || contractSignState.isLimit}
				className='contract-signing-page-send-code-again'
				onClick={() => {
					dispatch(submitSmsCodeAgain())
					setIsSended(true)
					setCanSendSmsAgain(false)
				}}
			>
				&nbsp;{contractSigningPageTextContents('contractSigningPage.smsCode.codeResend.heading')}&nbsp;
			</AppButton>
			<SendAgainSmsCodeStatusText contractSignState={contractSignState} contractSigningPageTextContents={props.contractSigningPageTextContents}/>
		</div>
	)
}

const ContractSigningPage = () => {
   const requestedAmount = useSelector((state: AppState) => state.offertState.requestedAmount)
   const email = useSelector(selectUserEmailFromState)
   const { downloadAgreement, handleOnContractSigningSubmit, dispatch, isSubmitting, contractSignState } = useContractSigningPageLogic()
   const textContent = useTextContents()

   return (

       <PageScreen
           title={{title: textContent('contractSigningPage.heading'), line: true}}
           panel={<LoanOfferSummaryPanel readOnly={true} loanVariant='selectedOffer' />}
       >
          <div className='contract-signing-page-container'>
            {/* <LoanCalculator isEditable={false} alwaysOpen={true}/> */}
            <div className='contract-signing-page-description'>
               <Paragraph>
                  <span dangerouslySetInnerHTML={{__html: textContent('contractSigningPage.mainContentTextUp.part_1')}} />
                  <button className='contract-signing-page-download-agreement-button' onClick={downloadAgreement}>
                     <b style={{color: 'var(--secondary-app-color)'}}>
                        &nbsp;{textContent('contractSigningPage.mainContentTextUp.part_2')}&nbsp;
                     </b>
                  </button>
                  <span dangerouslySetInnerHTML={{__html: textContent('contractSigningPage.mainContentTextUp.part_3')}} />
               </Paragraph>
               <Paragraph html={textContent('contractSigningPage.mainContentTextDown')} />
            </div>
         <CodeVerificationForm contractSigningPageTextContents={textContent} contractSignState={contractSignState} isLoading={isSubmitting} />
         <AppButton 
            id={Environment.CONTRACT_SIGN_BUTTON_ID} type='submit' 
            uiButtonType={UI_BUTTON_TYPE.PRIMARY} className='contract-signing-page-button' 
            onClick={() => handleOnContractSigningSubmit()}
            isLoading={isSubmitting}
         >
             Potwierdź
         </AppButton>
         <CodeVerificationFormSendSmsCodeAgain contractSignState={contractSignState} dispatch={dispatch} contractSigningPageTextContents={textContent} />
         </div>

         <HiddenInput id='requestedAmount' value={requestedAmount.toString()} />
         <HiddenInput id='email' value={email} />
       </PageScreen>
   )
}

export default ContractSigningPage